// ✅ COMPLETE FIXED hisService.js - Lab Results Filters Solution
import axios from 'axios';

const hisApi = axios.create({
    baseURL: process.env.VUE_APP_EXTERNAL_HIS || 'https://his.api.diab.vn',
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    }
});

const STATUS_MAP = {
    'FINAL': 'Hoàn thành',
    'PENDING': 'Đang chờ',
    'PRELIMINARY': 'Sơ bộ',
    'CORRECTED': 'Đã sửa',
    'CANCELLED': 'Đã hủy',
    'NORMAL': 'Bình thường',
    'ABNORMAL': 'Bất thường',
    'Bình thường': 'Bình thường',
    'Cao': 'Cao',
    'Thấp': 'Thấp'
};

export const hisLabService = {

    // Search patient by phone number
    async searchByPhone(phone) {
        try {
            const formattedPhone = this.formatPhoneNumber(phone);
            const { data } = await hisApi.get('/api/patpiidata/search-by-phone', {
                params: { phone: formattedPhone }
            });
            return data.data || [];
        } catch (error) {
            console.error('❌ Lỗi tìm kiếm bệnh nhân theo SĐT:', error);
            throw error;
        }
    },
    async searchByPatientId(idNumber) {
        try {
            const { data } = await hisApi.get('/api/patpiidata/search-by-patient-id', {
                params: { patientId: idNumber }
            });
            return data.data || [];
        } catch (error) {
            console.error('❌ Lỗi tìm kiếm bệnh nhân theo ID:', error);
            throw error;
        }
    },

    async getLabResults(phoneAndIdNumber, searchParams = {}) {
        try {
            // Hỗ trợ cả 2 cách gọi: getLabResults(phone) hoặc getLabResults({ phone, idNumber })
            const { phone, idNumber } = typeof phoneAndIdNumber === 'string'
                ? { phone: phoneAndIdNumber, idNumber: null }
                : phoneAndIdNumber;

            let results = [];

            // Thử tìm theo phone trước
            if (phone) {
                const formattedPhone = this.formatPhoneNumber(phone);
                const { data } = await hisApi.get('/api/clilabresults/detailed/by-patient-info', {
                    params: {
                        phone: formattedPhone,
                        ...searchParams
                    },
                });
                results = data.data || [];
            }

            // Nếu không có kết quả và có idNumber, fallback tìm theo idNumber
            if (results.length === 0 && idNumber) {
                const { data } = await hisApi.get('/api/clilabresults/detailed/by-patient-info', {
                    params: {
                        patientId: idNumber,
                        ...searchParams
                    },
                });
                results = data.data || [];
            }

            return results;
        } catch (error) {
            console.error('❌ Lỗi lấy kết quả xét nghiệm:', error);
            throw error;
        }
    },

    // ✅ FIX 1: Format lab results với date parsing đúng
    formatLabResultsToTable(rawData) {
        try {
            if (!Array.isArray(rawData) || rawData.length === 0) {
                return [];
            }

            // Sort by date descending
            const sorted = [...rawData].sort((a, b) => {
                const dateA = new Date(a.performedDate || a.testDate);
                const dateB = new Date(b.performedDate || b.testDate);
                return dateB - dateA;
            });

            return sorted.map((item, index) => {
                // ✅ Handle both API response formats
                const testDate = item.performedDate || item.testDate;
                const testValue = item.resultValue || item.testValue;
                const testUnit = item.resultUnit || item.unit;
                const category = item.serviceCategory || this.getCategoryFromTestCode(item.testCode || item.testName);
                
                return {
                    id: item.id || `${testDate}-${index}`,
                    date: this.formatDate(testDate), // ✅ Vietnamese format "dd/mm/yyyy HH:mm"
                    dateISO: testDate, // ✅ Keep ISO for filtering
                    dateTimestamp: new Date(testDate).getTime(), // ✅ Timestamp for easy comparison
                    category: category || 'Khác',
                    testName: item.testName || '--',
                    value: testValue || '--',
                    unit: testUnit || '--',
                    range: this.formatRange(item),
                    status: '', // ✅ Will be calculated by Vue component
                    statusText: '', // ✅ Will be calculated by Vue component
                    abnormalFlag: item.abnormalFlag || '',
                    resultStatus: item.resultStatus || item.testStatus || '',
                    _raw: item
                };
            });

        } catch (error) {
            console.error('❌ Lỗi format lab results:', error);
            return [];
        }
    },

    // ✅ FIX 2: Helper to get category from test code/name
    getCategoryFromTestCode(testCode) {
        if (!testCode) return 'Khác';
        
        const code = testCode.toLowerCase();
        
        // Glucose
        if (code.includes('glucose') || code.includes('đường')) return 'Đường huyết';
        
        // HbA1C
        if (code.includes('hba1c') || code.includes('hemoglobin')) return 'HbA1C';
        
        // Liver (Gan)
        if (code.includes('sgot') || code.includes('sgpt') || 
            code.includes('alt') || code.includes('ast') || 
            code.includes('gan')) return 'Gan';
        
        // Kidney (Thận)
        if (code.includes('creatinine') || code.includes('urea') || 
            code.includes('thận')) return 'Thận';
        
        // Lipid
        if (code.includes('cholesterol') || code.includes('triglyceride') || 
            code.includes('hdl') || code.includes('ldl')) return 'Lipid máu';
        
        // Blood pressure
        if (code.includes('huyết áp') || code.includes('blood pressure')) return 'Huyết áp';
        
        return 'Khác';
    },

    // ✅ FIX 3: Format range properly
    formatRange(item) {
        // Priority 1: Use referenceRange if exists
        if (item.referenceRange && 
            item.referenceRange !== 'null' && 
            item.referenceRange !== '' &&
            item.referenceRange !== '...') {
            return item.referenceRange;
        }
        
        // Priority 2: Build from min/max
        if (item.referenceMin !== null && item.referenceMax !== null) {
            return `${item.referenceMin} - ${item.referenceMax}`;
        }
        
        // Priority 3: Only max exists
        if (item.referenceMax !== null && item.referenceMin === null) {
            return `< ${item.referenceMax}`;
        }
        
        // Priority 4: Only min exists
        if (item.referenceMin !== null && item.referenceMax === null) {
            return `> ${item.referenceMin}`;
        }
        
        return 'Đang cập nhật';
    },

    // ✅ FIX 4: Format date to Vietnamese format
    formatDate(dateString) {
        if (!dateString) return '--';

        try {
            const date = new Date(dateString);
            
            // Check if valid date
            if (isNaN(date.getTime())) return '--';
            
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');

            return `${day}/${month}/${year} ${hours}:${minutes}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return '--';
        }
    },

    formatDateOnly(dateString) {
        if (!dateString) return '--';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '--';
            
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();

            return `${day}/${month}/${year}`;
        } catch (error) {
            return '--';
        }
    },

    getStatusText(status) {
        return STATUS_MAP[status] || status;
    },

    filterByStatus(results, status) {
        if (!status) return results;
        return results.filter(r => r.status === status);
    },

    filterByDateRange(results, startDate, endDate) {
        return results.filter(r => {
            const testDate = new Date(r._raw.performedDate || r._raw.testDate);
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;

            if (start && testDate < start) return false;
            if (end && testDate > end) return false;
            return true;
        });
    },

    getLatestResult(rawData) {
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
            return null;
        }

        const sorted = [...rawData].sort((a, b) => {
            const dateA = new Date(a.performedDate || a.testDate);
            const dateB = new Date(b.performedDate || b.testDate);
            return dateB - dateA;
        });

        const latest = sorted[0];
        return latest ? {
            testName: latest.testName,
            value: latest.resultValue || latest.testValue,
            unit: latest.resultUnit || latest.unit,
            status: latest.resultStatus || latest.testStatus,
            date: this.formatDate(latest.performedDate || latest.testDate)
        } : null;
    },

    groupByTestName(results) {
        const grouped = {};

        results.forEach(result => {
            const testName = result.testName;
            if (!grouped[testName]) {
                grouped[testName] = [];
            }
            grouped[testName].push(result);
        });

        return grouped;
    },

    getStatistics(rawData) {
        if (!Array.isArray(rawData) || rawData.length === 0) {
            return {
                total: 0,
                final: 0,
                pending: 0,
                normal: 0,
                abnormal: 0,
                categories: []
            };
        }

        const final = rawData.filter(r => 
            (r.testStatus === 'FINAL' || r.resultStatus === 'Hoàn thành')
        ).length;
        
        const pending = rawData.filter(r => 
            (r.testStatus === 'PENDING' || r.resultStatus === 'PENDING' || r.resultStatus === 'Đang chờ')
        ).length;
        
        const normal = rawData.filter(r => 
            (r.abnormalFlag === 'NORMAL' || r.resultStatus === 'Bình thường')
        ).length;
        
        const abnormal = rawData.filter(r => 
            (r.abnormalFlag === 'ABNORMAL' || r.testStatus === 'ABNORMAL')
        ).length;
        
        const categories = [...new Set(
            rawData.map(r => r.serviceCategory || this.getCategoryFromTestCode(r.testCode || r.testName))
                  .filter(Boolean)
        )];

        return {
            total: rawData.length,
            final,
            pending,
            normal,
            abnormal,
            categories
        };
    },

    // Medications methods
    async getActiveMedications(phoneAndIdNumber, searchParams = {}) {
        try {
            // Hỗ trợ cả 2 cách gọi: getActiveMedications(phone) hoặc getActiveMedications({ phone, idNumber })
            const { phone, idNumber } = typeof phoneAndIdNumber === 'string'
                ? { phone: phoneAndIdNumber, idNumber: null }
                : phoneAndIdNumber;

            let results = [];

            // Thử tìm theo phone trước
            if (phone) {
                const formattedPhone = this.formatPhoneNumber(phone);
                const { data } = await hisApi.get('/api/climedications', {
                    params: {
                        phone: formattedPhone,
                        ...searchParams
                    },
                });
                results = data.data || [];
            }

            // Nếu không có kết quả và có idNumber, fallback tìm theo idNumber
            if (results.length === 0 && idNumber) {
                const { data } = await hisApi.get('/api/climedications', {
                    params: {
                        patientId: idNumber,
                        ...searchParams
                    },
                });
                results = data.data || [];
            }

            return results;
        } catch (error) {
            console.error('❌ Lỗi lấy danh sách thuốc:', error);
            throw error;
        }
    },

    formatMedications(rawData) {
        try {
            if (!Array.isArray(rawData) || rawData.length === 0) {
                return [];
            }

            return rawData.map((item, index) => ({
                id: index,
                name: item.medicationName || item.brandName || '--',
                genericName: item.genericName || '--',
                brandName: item.brandName || '--',
                strength: item.strength || '--',
                form: item.form || '--',
                dosage: `${item.dosage || '--'} ${item.unit || ''}`.trim(),
                frequency: item.frequency || '--',
                quantity: `${item.quantityPrescribed || 0} ${item.unit || 'viên'}`,
                route: item.route || '--',
                instructions: item.usageInstructions || '--',
                prescribedBy: item.prescribedByName || 'Bác sĩ',
                startDate: this.formatDateOnly(item.startDate),
                endDate: this.formatDateOnly(item.endDate),
                expiryDate: this.formatDateOnly(item.endDate),
                notes: item.notes || '',
                unit: item.unit || '',
                daysRemaining: this.calculateDaysRemaining(item.endDate),
                isExpiringSoon: this.isExpiringSoon(item.endDate),
                _raw: item
            }));

        } catch (error) {
            console.error('❌ Lỗi format medications:', error);
            return [];
        }
    },

    calculateDaysRemaining(endDate) {
        if (!endDate || endDate.startsWith('0001-01-01')) return null;

        try {
            const end = new Date(endDate);
            const now = new Date();
            const diffTime = end - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            return diffDays;
        } catch (error) {
            return null;
        }
    },

    isExpiringSoon(endDate) {
        const days = this.calculateDaysRemaining(endDate);
        return days !== null && days > 0 && days <= 7;
    },

    groupMedicationsByForm(medications) {
        const grouped = {};

        medications.forEach(med => {
            const form = med.form || 'Khác';
            if (!grouped[form]) {
                grouped[form] = [];
            }
            grouped[form].push(med);
        });

        return grouped;
    },

    getMedicationStatistics(rawData) {
        if (!Array.isArray(rawData) || rawData.length === 0) {
            return {
                total: 0,
                expiringSoon: 0,
                expired: 0
            };
        }

        const formatted = this.formatMedications(rawData);

        const expiringSoon = formatted.filter(m => m.isExpiringSoon).length;
        const expired = formatted.filter(m => m.daysRemaining !== null && m.daysRemaining < 0).length;

        return {
            total: rawData.length,
            expiringSoon,
            expired
        };
    },

    // Doctor Notes methods
    async getDoctorNotes(phoneAndIdNumber, searchParams = {}) {
        try {
            // Hỗ trợ cả 2 cách gọi: getDoctorNotes(phone) hoặc getDoctorNotes({ phone, idNumber })
            const { phone, idNumber } = typeof phoneAndIdNumber === 'string'
                ? { phone: phoneAndIdNumber, idNumber: null }
                : phoneAndIdNumber;

            let results = [];

            // Thử tìm theo phone trước
            if (phone) {
                const formattedPhone = this.formatPhoneNumber(phone);
                const { data } = await hisApi.get('/api/cliemrheaders/doctor-notes', {
                    params: {
                        phone: formattedPhone,
                        ...searchParams
                    },
                });
                results = data.data || [];
            }

            // Nếu không có kết quả và có idNumber, fallback tìm theo idNumber
            if (results.length === 0 && idNumber) {
                const { data } = await hisApi.get('/api/cliemrheaders/doctor-notes', {
                    params: {
                        patientId: idNumber,
                        ...searchParams
                    },
                });
                results = data.data || [];
            }

            return results;
        } catch (error) {
            console.error('❌ Lỗi lấy ghi chú bác sĩ:', error);
            throw error;
        }
    },

    formatDoctorNotes(rawData) {
        try {
            if (!Array.isArray(rawData) || rawData.length === 0) {
                return [];
            }

            const sorted = [...rawData].sort((a, b) => {
                const dateA = new Date(a.noteDate);
                const dateB = new Date(b.noteDate);
                return dateB - dateA;
            });

            return sorted.map((item, index) => ({
                id: item.id || index,
                date: this.formatDate(item.noteDate),
                title: item.noteTitle || 'Ghi chú',
                author: item.authorName || 'Bác sĩ',
                content: item.noteContent || '',
                tags: item.noteTags,
                visitId: item.visitId,
                documentType: item.documentType,
                status: item.documentStatus,
                sections: this.parseNoteContent(item.noteContent),
                _raw: item
            }));

        } catch (error) {
            console.error('❌ Lỗi format doctor notes:', error);
            return [];
        }
    },

    parseNoteContent(content) {
        if (!content) return { items: [], actions: [] };

        try {
            const sections = {
                evolution: '',
                treatment: '',
                notes: '',
                items: [],
                actions: []
            };

            const lines = content.split('\n').filter(line => line.trim());
            const bullets = [];

            lines.forEach(line => {
                const trimmed = line.trim();

                if (trimmed.includes('Diễn biến') || trimmed.includes('Bệnh diễn')) {
                    sections.evolution = trimmed;
                } else if (trimmed.includes('Thuốc') || trimmed.includes('điều trị')) {
                    sections.treatment = trimmed;
                } else if (trimmed.includes('Ghi chú')) {
                    sections.notes = trimmed;
                } else {
                    if (trimmed.length > 0 && !trimmed.endsWith(':')) {
                        bullets.push(trimmed);
                    }
                }
            });

            sections.items = bullets.map((text, idx) => ({
                id: idx,
                text: text.replace(/^[-•*]\s*/, '')
            }));

            sections.actions = this.extractActions(content);

            return sections;

        } catch (error) {
            console.error('Error parsing content:', error);
            return { items: [{ id: 0, text: content }], actions: [] };
        }
    },

    extractActions(content) {
        const actions = [];

        const actionKeywords = [
            'đăng ký',
            'tái khám',
            'xét nghiệm',
            'kiểm tra',
            'theo dõi',
            'uống thuốc'
        ];

        const lines = content.toLowerCase().split('\n');

        lines.forEach((line, idx) => {
            actionKeywords.forEach(keyword => {
                if (line.includes(keyword)) {
                    actions.push({
                        id: idx,
                        text: keyword.charAt(0).toUpperCase() + keyword.slice(1),
                        type: keyword
                    });
                }
            });
        });

        return [...new Map(actions.map(a => [a.type, a])).values()].slice(0, 3);
    },

    groupNotesByType(notes) {
        const grouped = {};

        notes.forEach(note => {
            const type = note.documentType || 'OTHER';
            if (!grouped[type]) {
                grouped[type] = [];
            }
            grouped[type].push(note);
        });

        return grouped;
    },

    getLatestDoctorNote(rawData) {
        if (!Array.isArray(rawData) || rawData.length === 0) {
            return null;
        }

        const sorted = [...rawData].sort((a, b) => {
            const dateA = new Date(a.noteDate);
            const dateB = new Date(b.noteDate);
            return dateB - dateA;
        });

        return sorted[0];
    },

    // Lab Results - ALT and Creatinine
    async getLabResultsAlt(phoneAndIdNumber, searchParams = {}) {
        try {
            // Hỗ trợ cả 2 cách gọi: getLabResultsAlt(phone) hoặc getLabResultsAlt({ phone, idNumber })
            const { phone, idNumber } = typeof phoneAndIdNumber === 'string'
                ? { phone: phoneAndIdNumber, idNumber: null }
                : phoneAndIdNumber;

            let results = [];

            // Thử tìm theo phone trước
            if (phone) {
                const formattedPhone = this.formatPhoneNumber(phone);
                const { data } = await hisApi.get('/api/CliLabResults/getLabResultList', {
                    params: {
                        phone: formattedPhone,
                        type: 'alt',
                        ...searchParams
                    },
                });
                results = data.data || [];
            }

            // Nếu không có kết quả và có idNumber, fallback tìm theo idNumber
            if (results.length === 0 && idNumber) {
                const { data } = await hisApi.get('/api/CliLabResults/getLabResultList', {
                    params: {
                        patientId: idNumber,
                        type: 'alt',
                        ...searchParams
                    },
                });
                results = data.data || [];
            }

            return results;
        } catch (error) {
            console.error('❌ Lỗi lấy kết quả xét nghiệm ALT:', error);
            throw error;
        }
    },

    async getLabResultsCreatinine(phoneAndIdNumber, searchParams = {}) {
        try {
            // Hỗ trợ cả 2 cách gọi: getLabResultsCreatinine(phone) hoặc getLabResultsCreatinine({ phone, idNumber })
            const { phone, idNumber } = typeof phoneAndIdNumber === 'string'
                ? { phone: phoneAndIdNumber, idNumber: null }
                : phoneAndIdNumber;

            let results = [];

            // Thử tìm theo phone trước
            if (phone) {
                const formattedPhone = this.formatPhoneNumber(phone);
                const { data } = await hisApi.get('/api/CliLabResults/getLabResultList', {
                    params: {
                        phone: formattedPhone,
                        type: 'creatinine',
                        ...searchParams
                    },
                });
                results = data.data || [];
            }

            // Nếu không có kết quả và có idNumber, fallback tìm theo idNumber
            if (results.length === 0 && idNumber) {
                const { data } = await hisApi.get('/api/CliLabResults/getLabResultList', {
                    params: {
                        patientId: idNumber,
                        type: 'creatinine',
                        ...searchParams
                    },
                });
                results = data.data || [];
            }

            return results;
        } catch (error) {
            console.error('❌ Lỗi lấy kết quả xét nghiệm Creatinine:', error);
            throw error;
        }
    },

    getLatestAlt(rawData) {
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
            return null;
        }

        const altResults = rawData
            .filter(item =>
                item.testName === 'ALT' ||
                item.testCode === 'SGOT-SGPT' || 
                item.resultUnit === 'U/L' ||
                (item.testName && item.testName.toLowerCase().includes('alt') && item.resultUnit === 'U/L')
            )
            .sort((a, b) => new Date(b.performedDate) - new Date(a.performedDate));

        if (altResults.length > 0) {
            const latest = altResults[0];
            return {
                value: latest.resultValue || latest.value,
                unit: latest.resultUnit || latest.unit || 'U/L',
                date: this.isoToTimestamp(latest.performedDate)
            };
        }

        return null;
    },

    getLatestCreatinine(rawData) {
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
            return null;
        }

        const creatinineResults = rawData
            .filter(item =>
                item.testName === 'Thận' ||
                item.testCode === 'Creatinine' ||
                (item.testCode && item.testCode.toLowerCase().includes('creatinine'))
            )
            .sort((a, b) => new Date(b.performedDate) - new Date(a.performedDate));

        if (creatinineResults.length > 0) {
            const latest = creatinineResults[0];
            return {
                value: latest.resultValue || latest.value,
                unit: latest.resultUnit || latest.unit || 'mg/dL',
                date: this.isoToTimestamp(latest.performedDate)
            };
        }

        return null;
    },

    // Vital Signs
    async getVitalSignsByPhone(api, phoneAndIdNumber, type) {
        try {
            // Hỗ trợ cả 2 cách gọi: getVitalSignsByPhone(api, phone, type) hoặc getVitalSignsByPhone(api, { phone, idNumber }, type)
            const { phone, idNumber } = typeof phoneAndIdNumber === 'string'
                ? { phone: phoneAndIdNumber, idNumber: null }
                : phoneAndIdNumber;

            const isValidDate = (dateString) => {
                return dateString && !dateString.startsWith('0001-01-01');
            };

            const processRecords = (records) => {
                if (!records || records.length === 0) {
                    return null;
                }

                const validRecords = records.filter(record =>
                    isValidDate(record.latestExaminationDate)
                );

                if (validRecords.length === 0) {
                    return null;
                }

                const latestRecord = validRecords.reduce((latest, current) => {
                    const latestDate = new Date(latest.latestExaminationDate);
                    const currentDate = new Date(current.latestExaminationDate);
                    return currentDate > latestDate ? current : latest;
                });

                if (!isValidDate(latestRecord.nextExaminationDate)) {
                    latestRecord.nextExaminationDate = '';
                }

                return latestRecord;
            };

            let result = null;

            // Thử tìm theo phone trước
            if (phone) {
                const formattedPhone = this.formatPhoneNumber(phone);
                const response = await hisApi.get('/api/vitalsigns/by-phone', {
                    params: {
                        phone: formattedPhone,
                        type: type
                    }
                });
                result = processRecords(response.data.data.records);
            }

            // Nếu không có kết quả và có idNumber, fallback tìm theo idNumber
            if (!result && idNumber) {
                const response = await hisApi.get('/api/vitalsigns/by-phone', {
                    params: {
                        patientId: idNumber,
                        type: type
                    }
                });
                result = processRecords(response.data.data.records);
            }

            return result;

        } catch (error) {
            console.error(`Error fetching ${type} vital signs:`, error);
            throw error;
        }
    },

    formatGlucoseVitalSigns(rawData) {
        if (!rawData) {
            return {
                latestExaminationDate: '',
                nextExaminationDate: '',
                unit: '',
                value: null
            };
        }

        return {
            latestExaminationDate: rawData.latestExaminationDate || '',
            nextExaminationDate: rawData.nextExaminationDate || '',
            unit: rawData.unit || 'mg/dL',
            value: rawData.value || rawData.resultValue || null
        };
    },

    formatBloodPressureVitalSigns(rawData) {
        if (!rawData) {
            return {
                latestExaminationDate: '',
                nextExaminationDate: '',
                unit: '',
                value: null
            };
        }

        return {
            latestExaminationDate: rawData.latestExaminationDate || '',
            nextExaminationDate: rawData.nextExaminationDate || '',
            unit: rawData.unit || 'mmHg',
            value: rawData.value || rawData.resultValue || null
        };
    },

    // Utility methods
    formatPhoneNumber(phoneNumber) {
        if (!phoneNumber) return '';

        let cleaned = phoneNumber.replace(/[\s-]/g, '');

        if (cleaned.startsWith('+84')) {
            cleaned = '0' + cleaned.slice(3);
        } else if (cleaned.startsWith('84')) {
            cleaned = '0' + cleaned.slice(2);
        }

        return cleaned;
    },

    isoToTimestamp(isoString) {
        if (!isoString) return null;

        const ms = Date.parse(isoString);
        if (isNaN(ms)) return null;

        return Math.floor(ms / 1000);
    },
};